import {
  useGetTechnicalMessageCountQuery,
  useGetTechnicalMessageQuery,
  useHideTechnicalMessageMutation,
} from '@api/technicalMessage.api';
import { TechnicalMessage } from '@api/types/technicalMessage/TechnicalMessage';
import { TextCodes } from '@api/types/translation/TextCodes';
import { Button, Html, SmartLink, Title } from '@components';
import { useAppSelector, useAuth } from '@hooks';
import ClearIcon from '@mui/icons-material/Clear';
import { Card, CardContent, Container, IconButton, Stack, Typography } from '@mui/material';
import { getSpecialPage } from '@store/app/app.slice';
import { t } from '@translations';
import { useEffect, useState } from 'react';

export const TechnicalMessageAlertListBlock = () => {
  const { currentData: messagesData } = useGetTechnicalMessageQuery({
    offset: 0,
    limit: 1000,
    filters: [{ filterName: 'isnew', value: 'true' }],
    sorting: {
      fieldToSort: 'CreatedOn',
      sortingDirection: 'desc',
    },
  });
  const { currentData: statusData } = useGetTechnicalMessageCountQuery();
  const { isDealerUser, isServiceUser, isOEMUser, isBeremaUser } = useAuth();
  const isCustomer = isDealerUser || isOEMUser || isServiceUser;
  const [readMessageIds, setReadMessageIds] = useState<number[]>([]);
  const dealerTechnicalMessagePage = useAppSelector(
    getSpecialPage('dealerTechnicalMessageListPage')
  );
  const beremaTechnicalMessagePage = useAppSelector(
    getSpecialPage('beremaTechnicalMessageListPage')
  );
  const technicalMessagePage = isBeremaUser
    ? beremaTechnicalMessagePage
    : dealerTechnicalMessagePage;
  const unreadMessages = messagesData?.data?.technicalMessages.filter(
    (message) => !readMessageIds.includes(message.id) && message.isNew
  );

  const handleHideMessage = (messageId: number) => {
    const updatedMessageIds = [...readMessageIds, messageId];
    localStorage.setItem('readTechnicalMessagesIds', JSON.stringify(updatedMessageIds));
    setReadMessageIds(updatedMessageIds);
  };

  useEffect(() => {
    const storedMessageIds = JSON.parse(localStorage.getItem('readTechnicalMessagesIds') || '[]');
    setReadMessageIds(storedMessageIds);
  }, []);

  return isCustomer && unreadMessages && unreadMessages.length > 0 ? (
    <Container sx={{ my: 2 }}>
      {unreadMessages.slice(0, 3).map((message: TechnicalMessage) => (
        <MessageCard onMessageHide={handleHideMessage} key={message.id} {...message} />
      ))}
      {statusData && statusData > 3 && unreadMessages.length > 0 ? (
        <Typography display='flex'>
          {t(TextCodes.TechnicalMessage.Block.TechnicalMessageTotalInfo, {
            messagesCount: (statusData - unreadMessages.slice(0, 3).length).toString(),
          })}
          &nbsp;
          <SmartLink href={technicalMessagePage}>
            {t(TextCodes.TechnicalMessage.Block.TechnicalMessagePage)}
          </SmartLink>
        </Typography>
      ) : null}
    </Container>
  ) : null;
};

const hideButtonStyles = {
  minWidth: '100px !important',
  fontSize: '14px !important',
  height: '34px !important',
};

type MessageCardProps = TechnicalMessage & {
  onMessageHide: (messageId: number) => void;
};

const MessageCard = ({ onMessageHide, ...props }: MessageCardProps) => {
  const { id, text, messageType, title } = props;
  const [trigger, { isLoading }] = useHideTechnicalMessageMutation();

  const technicalMessageDetailsBaseUrl = useAppSelector(
    getSpecialPage('technicalMessageDetailsPage')
  ) as string;
  const technicalMessageDetailsHref = technicalMessageDetailsBaseUrl + id;

  const handleMarkAsRead = (messageId: number) => {
    try {
      trigger(messageId).then(() => {
        onMessageHide(messageId);
      });
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Card sx={{ mt: 2, position: 'relative' }} variant='outlined'>
      <IconButton
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          zIndex: 1,
          '&:hover': {
            backgroundColor: 'transparent',
          },
        }}
        onClick={() => onMessageHide(id)}
      >
        <ClearIcon fontSize='inherit' />
      </IconButton>
      <CardContent sx={{ p: 2 }}>
        <Typography variant='caption' color='textSecondary' sx={{ textTransform: 'uppercase' }}>
          {messageType}
        </Typography>
        <Title variant='h4' noDot sx={{ mb: 1 }}>
          {title}
        </Title>

        <Html
          key={id}
          html={text}
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 5,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        />

        <Stack direction='row' justifyContent='flex-start' mt={2} gap={2}>
          <Button variant='primary' href={technicalMessageDetailsHref} sx={hideButtonStyles}>
            {t(TextCodes.TechnicalMessage.Block.Open)}
          </Button>
          <Button
            variant='ghost'
            busy={isLoading}
            onClick={() => handleMarkAsRead(id)}
            sx={hideButtonStyles}
          >
            {t(TextCodes.TechnicalMessage.Block.MarkAsRead)}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};
